import { Component, ElementRef, ViewChild, AfterViewInit, OnInit, OnDestroy, Renderer2, Inject } from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { SuiteTechnologiesComponent } from '../../../general/suite-technologies/suite-technologies.component';
import { FaqComponent } from '../../../shared/faq/faq.component';
import { FaqItem } from '../../../models/faq.model';
import { ExploreMoreServicesComponent } from '../explore-more-services/explore-more-services.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ContactUsComponent } from '../../../shared/contact-us/contact-us.component';

interface CardData {
  icon: string;
  title: string;
  content: string;
}
interface TimelineCard {
  id: number;
  year: string;
  title: string;
  description: string;
  image: string;
}

@Component({
  selector: 'app-digital-marketing',
  imports: [CommonModule, SuiteTechnologiesComponent, FaqComponent, ExploreMoreServicesComponent],
  standalone: true,
  templateUrl: './digital-marketing.component.html',
  styleUrl: './digital-marketing.component.scss'
})
export class DigitalMarketingComponent implements AfterViewInit, OnInit, OnDestroy {
  private jsonLdScript: any;
  constructor(
    private modalService: NgbModal,
    @Inject(DOCUMENT) private _document: Document,
    private _renderer: Renderer2,
  ) { }

  @ViewChild('heroVideo') heroVideo!: ElementRef<HTMLVideoElement>;
  goToCaseStudies(): void {

  }
  ngOnDestroy(): void {
    this.removeSchemaMarkup();
  }
  ngOnInit(): void {
    this.injectSchemaMarkup();
  }
  injectSchemaMarkup(): void {
    const digitalMarketingSchema = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://www.kalpitatechnologies.com/company-services/digital-marketing#webpage",
          "url": "https://www.kalpitatechnologies.com/company-services/digital-marketing",
          "name": "Digital Marketing Services | SEO, Social Media & PPC | Kalpita Technologies",
          "description": "Full-service digital marketing agency offering SEO, social media marketing, content marketing, paid ads, branding, and creative design.",
          "isPartOf": { "@id": "https://www.kalpitatechnologies.com/#website" },
          "inLanguage": "en-US"
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://www.kalpitatechnologies.com/company-services/digital-marketing#breadcrumbs",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.kalpitatechnologies.com/" },
            { "@type": "ListItem", "position": 2, "name": "Services", "item": "https://www.kalpitatechnologies.com/company-services" },
            { "@type": "ListItem", "position": 3, "name": "Digital Marketing", "item": "https://www.kalpitatechnologies.com/company-services/digital-marketing" }
          ]
        },
        {
          "@type": "Service",
          "@id": "https://www.kalpitatechnologies.com/company-services/digital-marketing#service",
          "name": "Digital Marketing Services",
          "serviceType": "Digital Marketing",
          "provider": { "@id": "https://www.kalpitatechnologies.com/#organization" },
          "description": "End-to-end digital marketing services covering SEO, social media, content, paid ads, branding, and creative design."
        },
        {
          "@type": "HowTo",
          "@id": "https://www.kalpitatechnologies.com/company-services/digital-marketing#process",
          "name": "How Our Digital Marketing Process Works",
          "step": [
            { "@type": "HowToStep", "position": 1, "name": "Discovery & Strategy", "text": "Analyze business, audience, and competitors to design a custom strategy." },
            { "@type": "HowToStep", "position": 2, "name": "Content Development", "text": "Craft high-quality content and visuals optimized for engagement." },
            { "@type": "HowToStep", "position": 3, "name": "SEO & Optimization", "text": "Implement technical fixes and AEO for top search rankings." },
            { "@type": "HowToStep", "position": 4, "name": "Execution", "text": "Deploy campaigns across all social and digital channels." },
            { "@type": "HowToStep", "position": 5, "name": "Monitoring", "text": "Weekly optimization and analytics review for consistent growth." }
          ]
        },
        {
          "@type": "FAQPage",
          "@id": "https://www.kalpitatechnologies.com/company-services/digital-marketing#faq",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "What are the benefits of digital marketing?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Digital marketing helps businesses increase visibility, generate quality leads, improve customer engagement, and scale faster through targeted online channels."
              }
            }
          ]
        },
        {
          "@type": "Organization",
          "@id": "https://www.kalpitatechnologies.com/#organization",
          "name": "Kalpita Technologies",
          "url": "https://www.kalpitatechnologies.com/",
          "logo": { "@type": "ImageObject", "url": "https://www.kalpitatechnologies.com/assets/common/black-logo.png" }
        }
      ]
    };

    this.jsonLdScript = this._renderer.createElement('script');
    this.jsonLdScript.type = 'application/ld+json';
    this.jsonLdScript.text = JSON.stringify(digitalMarketingSchema);
    this._renderer.appendChild(this._document.head, this.jsonLdScript);
  }

  removeSchemaMarkup(): void {
    if (this.jsonLdScript) {
      this._renderer.removeChild(this._document.head, this.jsonLdScript);
      this.jsonLdScript = null;
    }
  }
  private forcePlayHeroVideo() {
    const video = this.heroVideo?.nativeElement;
    if (!video) return;

    video.muted = true;           // required for autoplay
    video.playsInline = true;

    const play = () => {
      video.play().catch(() => {
        // retry if browser blocks
        setTimeout(() => video.play().catch(() => { }), 300);
      });
    };

    // Ensure it runs after Angular rendering
    setTimeout(play, 100);
  }
  digitalmarketingFaqs: FaqItem[] = [
    {
      question: 'What are the benefits of digital marketing?',
      answer: `Digital marketing helps businesses increase visibility, generate leads, 
      improve engagement, and scale faster through targeted online channels.`
    },
    {
      question: 'How to choose a digital marketing agency?',
      answer: `Evaluate expertise, industry experience, tools used, past results, custom strategy 
      capability, and transparency in reporting.`
    },
    {
      question: 'What makes Kalpita different from other digital marketing companies?',
      answer: `Our AI-driven SEO, full-service execution, in-house creative team, and 
      cross-industry expertise give clients a competitive edge.`
    },
    {
      question: 'Do you offer SEO audits and website fixes?',
      answer: `Yes. We provide complete SEO audits, technical optimizations, content fixes, 
      and website revamps.`
    },
    {
      question: 'Do you offer services in the USA and India?',
      answer: `Absolutely. We work with global clients, with strong delivery capabilities in both regions.`
    }
  ];

  @ViewChild('benefitCardsContainer', { static: false }) private benefitCardsContainer!: ElementRef;

  marketingFeatures = [
    {
      category: 'Social Media Marketing',
      items: [
        'Social Media Strategy',
        'SM Post Creation',
        'SM Monitoring & Engagement',
        'Account Creation & Platform Setup',
        'Community Building',
        'Influencer Marketing & Outreach'
      ]
    },
    {
      category: 'Content Marketing',
      items: [
        'Content Writing & Editing (Blogs, Case Studies, Whitepapers)',
        'Content Strategy & Planning',
        'Guest Posting',
        'Press Releases & Thought Leadership'
      ]
    },
    {
      category: 'SEO Services',
      items: [
        'Website SEO Audit',
        'Keyword Research & Strategy',
        'On-Page SEO & Off-Page SEO',
        'Technical SEO & Link Building',
        'Local SEO Services',
        'Competitive Analysis',
        'AI SEO & AEO Optimization',
        'Website Fixes, Updates',
        'Website Revamp & UX Optimization',
        'Business Listings '
      ]
    },
    {
      category: 'Creative & Visual Design',
      items: [
        'UI/UX Design',
        'Graphic Design',
        'Motion Graphics',
        'Videography',
        'Video Editing',
        'Photography'
      ]
    },
    {
      category: 'Paid Ads & PPC',
      items: [
        'Google Ads, Meta Ads, LinkedIn Ads',
        'PPC Campaign Setup',
        'Lead Generation & Funnel Campaigns',
        'Ad Copywriting & Creative Design',
        'Weekly Monitoring & Optimization'
      ]
    },
    {
      category: 'Branding Services',
      items: [
        'Brand Identity',
        'Brand Strategy & Consulting',
        'Logo Design',
        'Corporate Branding',
        'Marketing Collateral (Decks, Brochures, Flyers, Standee, Business Cards, Videos)'
      ]
    }
  ];

  timelinecards: TimelineCard[] = [
    {
      id: 1,
      year: '2020',
      title: 'Accelerating Retirement Planning with Intelligent Data Automation',
      description: 'A leading financial advisory platform transformed its manual plan fee analysis into an automated, real-time process. By integrating OCR-based data extraction and Azure-powered benchmarking, the new app empowered over 200 advisors to generate reports in under 5 minutes, achieving 88% faster processing and reducing data errors by 75%.',
      image: 'https://images.unsplash.com/photo-1573164713714-d95e436ab8d6?w=400&h=300&fit=crop'
    },
    {
      id: 2,
      year: '2023',
      title: 'Empowering Field Sales Teams with an Offline-First Mobile Solution',
      description: 'A global IoT provider enhanced its sales operations with a cross-platform field sales app built using React Native. Equipped with offline data sync, digital quotes, and real-time sales dashboards, the solution improved lead conversion by 30%, shortened deal cycles by 13 days, and boosted sales productivity by $500K in just six months.',
      image: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=400&h=300&fit=crop'
    },
    {
      id: 3,
      year: '2025',
      title: 'Boosting SME Growth Through White-Label E-Commerce Apps',
      description: 'Small and medium retailers gained independence from high marketplace fees with a customizable Flutter-based e-commerce app. Featuring integrated payments, push notifications, and AI-driven recommendations, the platform enabled 25+ businesses to launch branded storefronts within months. The app achieved a 50% add-to-cart rate and a 6% conversion rate, outperforming the industry average of 3–4% and helping partners build stronger direct-to-consumer engagement.',
      image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=400&h=300&fit=crop'
    },
    {
      id: 4,
      year: '2026',
      title: 'Revolutionizing Skincare Retail with AI & AR-Powered Personalization',
      description: 'A leading skincare brand leveraged AI, ML, and AR technologies to deliver personalized product experiences. With real-time skin analysis, virtual try-ons, and smart reorder features, the app achieved 50K+ downloads, a 65% drop in product returns, and a 25% boost in average basket value, driving $750K in mobile revenue.',
      image: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=400&h=300&fit=crop'
    }
    // {
    //   id: 5,
    //   year: '2027',
    //   title: 'Global Reach',
    //   description: 'Expanding our presence globally with cutting-edge technology solutions.',
    //   image: 'https://images.unsplash.com/photo-1573164713714-d95e436ab8d6?w=400&h=300&fit=crop'
    // }
  ];

  selectedIndex = 1; // Start with middle card selected

  // THIS IS THE FIX: All instances of 'this.cards' are changed to 'this.timelinecards'
  get visibleCards(): Array<{ card: TimelineCard, position: 'top' | 'center' | 'bottom' }> {
    const result = [];

    // Top card
    if (this.selectedIndex > 0) {
      result.push({
        card: this.timelinecards[this.selectedIndex - 1],
        position: 'top' as const
      });
    }

    // Center card (selected)
    result.push({
      card: this.timelinecards[this.selectedIndex],
      position: 'center' as const
    });

    // Bottom card
    if (this.selectedIndex < this.timelinecards.length - 1) {
      result.push({
        card: this.timelinecards[this.selectedIndex + 1],
        position: 'bottom' as const
      });
    }

    return result;
  }

  // THIS IS THE FIX: Changed 'this.cards' to 'this.timelinecards'
  get showCarouselDots(): boolean {
    return this.timelinecards.length > 3;
  }

  selectCard(position: 'top' | 'center' | 'bottom'): void {
    if (position === 'top' && this.selectedIndex > 0) {
      this.selectedIndex--;
    } else if (position === 'bottom' && this.selectedIndex < this.timelinecards.length - 1) {
      this.selectedIndex++;
    }
  }

  // THIS IS THE FIX: Changed 'this.cards' to 'this.timelinecards'
  goToCard(index: number): void {
    if (index >= 0 && index < this.timelinecards.length) {
      this.selectedIndex = index;
    }
  }

  get canScrollUp(): boolean {
    return this.selectedIndex > 0;
  }

  get canScrollDown(): boolean {
    return this.selectedIndex < this.timelinecards.length - 1;
  }

  whyChooseFeatures = [
    {
      icon: '../../../../assets/services/digital-marketing/whyChooseUs/CompleteIn-HouseTeam.png',
      title: 'Complete In-House Team',
      description: 'From SEO and content to social media, paid ads, branding, and creative design, we manage every stage of your digital funnel, ensuring seamless execution and measurable growth.'
    },
    {
      icon: '../../../../assets/services/digital-marketing/whyChooseUs/AI-PoweredSEOAEOforHigherSearchVisibility.png',
      title: 'AI-Powered SEO & AEO for Higher Search Visibility',
      description: 'We use advanced AI-driven SEO and Answer Engine Optimization (AEO) to help your brand rank not only on Google but also on AI search platforms, driving consistent, high-intent organic traffic.'
    },
    {
      icon: '../../../../assets/services/digital-marketing/whyChooseUs/Data-DrivenStrategyTailoredtoYourBusiness.png',
      title: 'Data-Driven Strategy Tailored to Your Business',
      description: 'Every campaign begins with deep audience insights, competitor analysis, and industry benchmarks, enabling us to design strategies that deliver predictable performance and real business impact.'
    },
    {
      icon: '../../../../assets/services/digital-marketing/whyChooseUs/CreativeExcellencewithHigh-QualityContentVisuals.png',
      title: 'Creative Excellence with High-Quality Content & Visuals',
      description: 'Our in-house team of writers, designers, videographers, and motion artists crafts premium content that elevates your brand identity, boosts engagement, and strengthens online presence.'
    },
    {
      icon: '../../../../assets/services/digital-marketing/whyChooseUs/TransparentReportingWeeklyPerformanceOptimization.png',
      title: 'Transparent Reporting & Weekly Performance Optimization',
      description: 'We provide clear dashboards, weekly updates, and actionable insights while continuously refining campaigns to maximize ROI and reduce your cost per lead.'
    },
    {
      icon: '../../../../assets/services/digital-marketing/whyChooseUs/GlobalDeliverywithIndustry-SpecificExpertise.png',
      title: 'Global Delivery with Industry-Specific Expertise',
      description: 'With strong presence in India and the USA, we understand exactly how to market your business in competitive markets.'
    }
  ];

  developmentProcess = [
    {
      icon: '../../../../assets/services/digital-marketing/developmentProcess/DiscoveryStrategy.png',
      title: 'Discovery & Strategy',
      description: 'We analyze your business, audience, competitors, and goals to design a custom digital strategy.'
    },
    {
      icon: '../../../../assets/services/digital-marketing/developmentProcess/DiscoveryStrategy.png',
      title: 'Content & Creative Development',
      description: 'Our writers, designers, and strategists craft high-quality content and visuals for multi-platform use.'
    },
    {
      icon: '../../../../assets/services/digital-marketing/developmentProcess/SEO_Optimization.png',
      title: 'SEO & Optimization',
      description: 'We implement on-page SEO, technical fixes, keyword strategies, and AEO for top rankings.'
    },
    {
      icon: '../../../../assets/services/digital-marketing/developmentProcess/ExecutionAcrossChannels.png',
      title: 'Execution Across Channels',
      description: 'From social media and blogs to ads and branding, we deploy campaigns for maximum impact.'
    },
    {
      icon: '../../../../assets/services/digital-marketing/developmentProcess/MonitoringImprovement.png',
      title: 'Monitoring & Improvement',
      description: 'Weekly optimization, analytics review, and continuous enhancements ensure consistent growth.'
    }
  ];

  // Service Overview section data and logic
  services = [
    {
      title: 'Faster Time to Market',
      description: 'We design software that grows with your business and adapts to new technologies.',
      icon: 'https://i.ibb.co/yBNtD9z/future-ready.png'
    },
    {
      title: 'Secure & Compliant',
      description: 'Our agile development process ensures quick and efficient delivery of your software.',
      icon: 'https://i.ibb.co/yBNtD9z/future-ready.png'
    },
    {
      title: 'Innovation at Core',
      description: 'We build robust and secure software that complies with industry standards.',
      icon: 'https://i.ibb.co/yBNtD9z/future-ready.png'
    },
    {
      title: 'Future-Ready Architecture',
      description: 'We leverage the latest technologies to build innovative and cutting-edge software.',
      icon: 'https://i.ibb.co/yBNtD9z/future-ready.png'
    },
    {
      title: 'End to End Support',
      description: 'We provide comprehensive support and maintenance services for your software.',
      icon: 'https://i.ibb.co/yBNtD9z/future-ready.png'
    }
  ];
  selectedServiceIndex = 3; // Default to "Future-Ready Architecture"

  // Pre-defined positions for the icons on the arc and the active position
  private arcPositions = [
    { x: -180, y: 80 },
    { x: -110, y: -20 },
    { x: 0, y: -60 },
    { x: 110, y: -20 },
    { x: 180, y: 80 }
  ];
  private activePosition = { x: 0, y: 150 };

  selectService(index: number): void {
    this.selectedServiceIndex = index;
  }

  getPointTransform(index: number): string {
    let pos;
    if (index === this.selectedServiceIndex) {
      pos = this.activePosition;
    } else {
      pos = this.arcPositions[index];
    }
    return `translate(${pos.x}px, ${pos.y}px)`;
  }

  readMore(): void {
  }

  ngAfterViewInit(): void {
    this.forcePlayHeroVideo();
    // MODIFIED: Added .dm-feature-card to the selector to target the marketing features
    const cards = document.querySelectorAll('.feature-card, .dm-feature-card');

    cards.forEach(card => {
      const cardEl = card as HTMLElement;

      const mouseMoveHandler = (e: MouseEvent) => {
        const rect = cardEl.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        // Calculate center
        const xc = rect.width / 2;
        const yc = rect.height / 2;

        // Max rotation degrees
        const maxRotate = 10;

        // Calculate rotation based on cursor position
        const rotateY = ((x - xc) / xc) * maxRotate;
        const rotateX = ((yc - y) / yc) * maxRotate; // Inverse Y axis

        // Apply transform
        cardEl.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.02, 1.02, 1.02)`;
      };

      const mouseLeaveHandler = () => {
        // Reset to flat state
        cardEl.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)';
      };

      const mouseEnterHandler = () => {
        // Smooth entry
        cardEl.style.transition = 'transform 0.1s ease-out, box-shadow 0.2s ease-in-out, background 0.2s ease';
      };

      // Attach Listeners
      cardEl.addEventListener('mousemove', mouseMoveHandler as EventListener);
      cardEl.addEventListener('mouseleave', mouseLeaveHandler as EventListener);
      cardEl.addEventListener('mouseenter', mouseEnterHandler as EventListener);

      // Optional: Disable on touch devices for better scrolling
      if (window.matchMedia('(hover: none) and (pointer: coarse)').matches) {
        cardEl.removeEventListener('mousemove', mouseMoveHandler as EventListener);
        cardEl.style.transform = 'none';
      }
    });
  }

  getAFreeQuote(): void {
    this.modalService.open(ContactUsComponent, {
      centered: true,
      backdrop: 'static',
      windowClass: 'contact-modal'
    });
  }

  bookAFreeCall(): void {
    this.getAFreeQuote();
  }
}
